import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Brain, Fingerprint, ShieldCheck, Orbit } from "lucide-react";

const features = [
  {
    icon: Brain,
    title: "Predictive Emotional Analytics",
    description:
      "Real-time biosignal processing to anticipate and pre-empt emotional shifts.",
  },
  {
    icon: Fingerprint,
    title: "Digital Olfactory Twin",
    description:
      "AI-generated scent profiles that adapt to your neurological state.",
  },
  {
    icon: ShieldCheck,
    title: "Privacy-First Edge AI",
    description:
      "All data processed locally on-device. Zero cloud dependency.",
  },
  {
    icon: Orbit,
    title: "Zero-G Ergonomics",
    description:
      "Engineered for gravitational variance — from Earth to Mars.",
  },
];

const TechSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="technology"
      data-testid="tech-section"
      ref={ref}
      className="relative z-10 py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 lg:mb-20"
        >
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-cyanGlow mb-4">
            Core Technology
          </p>
          <h2 className="metallic-text text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
            Engineered for Resilience
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.15 * i }}
                data-testid={`tech-feature-${i}`}
                className="glass-card rounded-2xl p-8 group"
              >
                <div className="w-12 h-12 rounded-xl bg-cyanGlow/10 border border-cyanGlow/20 flex items-center justify-center mb-6 group-hover:bg-cyanGlow/15 transition-colors duration-300">
                  <Icon
                    size={22}
                    className="text-cyanGlow icon-glow"
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="text-base font-bold text-offWhite tracking-tight mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-500 leading-relaxed font-light">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TechSection;
